import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { PlusCircle } from 'lucide-react';

import { ServiceType, Subtype } from '../../types/ServiceAccordion.type';
import ServiceSubtypeCard from './ServiceSubtypeCard';
import SubTypeDialog from '../FormDialog/SubtypeDialog';
import { SuccessResponse } from '../../types/CourseStageAccordion.type';
import { apiDelete } from '../../utils/apiClient';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';

interface ServiceSubtypeListProps {
  service: ServiceType;
  subtypes: Subtype[];
  fetchServiceTypes: () => void;
}

const ServiceSubtypeList: React.FC<ServiceSubtypeListProps> = ({
  service,
  subtypes,
  fetchServiceTypes,
}) => {
  const [openSubtypeDialog, setOpenSubtypeDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedSubtype, setSelectedSubtype] = useState<Subtype | null>(null);

  const handleCloseSubtypeDialog = () => {
    setOpenSubtypeDialog(false);
    setSelectedSubtype(null);
  };

  const handleConfirmDeleteSubtype = async (force: boolean) => {
    try {
      const res = await apiDelete<SuccessResponse>(
        `/service/subtype/delete/${selectedSubtype?.id}?force=${force}`,
      );

      notifyAPIClient(res.message);
      setOpenDeleteDialog(false);
      setSelectedSubtype(null);
      fetchServiceTypes();
    } catch (error) {
      console.error('Error deleting subtype:', error);
      notifyFailure();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body1" component="h3" fontWeight="bold">
          {service.name} Service Types
        </Typography>

        <Button
          size="medium"
          variant="contained"
          color="primary"
          startIcon={<PlusCircle size={16} />}
          sx={{
            color: 'white',
          }}
          onClick={() => {
            setSelectedSubtype(null);
            setOpenSubtypeDialog(true);
          }}
        >
          Add Service Type
        </Button>
      </Box>

      {subtypes.length === 0 ? (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ fontStyle: 'italic', py: 2 }}
        >
          No service types available
        </Typography>
      ) : (
        subtypes.map((subtype) => (
          <ServiceSubtypeCard
            key={subtype.id}
            subtype={subtype}
            onEdit={(subtype) => {
              setSelectedSubtype(subtype);
              setOpenSubtypeDialog(true);
            }}
            onDelete={(subtype) => {
              setSelectedSubtype(subtype);
              setOpenDeleteDialog(true);
            }}
            fetchServiceTypes={fetchServiceTypes}
          />
        ))
      )}

      <SubTypeDialog
        open={openSubtypeDialog}
        handleClose={handleCloseSubtypeDialog}
        subtype={selectedSubtype}
        serviceTypeId={service.id}
        fetchServiceTypes={fetchServiceTypes}
      />

      {/* Delete dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Service</DialogTitle>
        <DialogContent>
          Do you want to delete this service type? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="contained"
            color="error"
          >
            Close
          </Button>
          <Button
            onClick={() => handleConfirmDeleteSubtype(false)}
            variant="contained"
            color="primary"
            className="!text-white"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceSubtypeList;
